import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { DecorativeWave } from '@/src/renderer/components/ui/DecorativeWave'
import type { RecurrenceRule, Task } from '@/src/renderer/lib/db/models'
import { recurrenceUpdate } from '@/src/renderer/lib/recurrence'
import { WhenDropdownContent } from '../WhenDropdown'
import { DurationDropdownContent } from '../DurationDropdown'
import { DeadlineDropdownContent } from '../DeadlineDropdown'
import { RecurrenceDropdownContent } from '../RecurrenceDropdown'
import type { DetailsSection as DetailsSectionModel, DetailsBodyId } from './useDetailsSections'
import type { DetailsValues } from './detailsValues'
import type { TaskDetailsUpdate } from './TaskDetails.types'

/** In den Aufgaben-Details schließt nichts — alles bleibt offen (CONTEXT.md, Aufgaben-Details). */
const noop = () => {}

/**
 * **Der Weg zurück auf „nicht gesetzt"**, je Bereich.
 *
 * Er steht IM Kasten, nicht im Gruppentitel — er gehört zum Wert, nicht zur Überschrift
 * (ADR 0026): Wann, Deadline und Dauer tragen einen Fuß mit „Entfernen", sobald ein Wert
 * dasteht; der Wiederkehr-Wähler trägt „Nie" als ersten Eintrag seines Auswahlfelds. Bis
 * zum 19.09.2026 stand dafür ein kleiner Papierkorb rechts im Gruppentitel.
 */
const REMOVE_PATCH: Record<DetailsBodyId, Partial<Omit<Task, 'id'>>> = {
  start: { startDate: null, startTime: null, startPeriod: null },
  duration: { estimatedDuration: null, durationMode: null },
  deadline: { dueDate: null, dueTime: null, duePeriod: null },
  recurrence: { recurrence: null },
}

/**
 * **Der Inhalt steht als weiße Karte da** — Fläche, Rahmen, Schatten, 12px eingerückt
 * (Figma `5362:160093`). Seit der Etappe 4 alle vier Bereiche.
 */
const BOXED: ReadonlySet<DetailsBodyId> = new Set(['start', 'deadline', 'duration', 'recurrence'])

export interface DetailsSectionProps {
  /** Ein Eintrag mit `place: 'body'` — die vier Bereiche, die offen untereinander rollen. */
  section: DetailsSectionModel
  /**
   * Die Id der ersten Aufgabe der Auswahl — damit wird geschrieben; `onUpdate` weitet auf
   * die übrigen aus. Bewusst nur die Id und nicht die ganze Aufgabe: Die WERTE kommen aus
   * `values` und gehören der Auswahl, nicht ihrem ersten Mitglied.
   */
  anchorId: string
  /** Was ALLE Ausgewählten tragen — siehe {@link detailsValues}. */
  values: DetailsValues
  onUpdate: TaskDetailsUpdate
  /**
   * Die **Fokuszeit** in Sekunden — gebuchte Abschnitte.
   *
   * Sie geht in das **Dauer**-Werkzeug hinein und steht dort neben der
   * veranschlagten Dauer: „9 Min 53 Sek von 10 Min" (Nutzer-Vorgabe,
   * 2026-09-18). Als eigene Zeile darunter sagte keine der beiden Zahlen etwas
   * über die andere; ein eigener Bereich brauchte zudem ein Zeichen in der
   * Leiste und eine Sprungmarke — viel Gerüst für eine Zahl.
   *
   * **Nur bei EINER ausgewählten Aufgabe.** `values` sagt sonst, was alle
   * tragen; eine Summe über drei Aufgaben wäre eine andere Aussage und würde
   * für dieselbe gehalten.
   */
  focusedSeconds?: number | null
  /**
   * **„Ruhen lassen"** im Wann-Kasten. Der Aufrufer reicht es nur, solange
   * mindestens eine ausgewählte Aufgabe wach ist (CONTEXT.md, gemischte Auswahl).
   */
  onRest?: () => void
  /** Ob alle gemeinten Aufgaben ruhen — die Zeile heißt dann „Aufwecken …". */
  resting?: boolean
  /**
   * Eine Unteraufgabe unter einer wiederkehrenden Aufgabe (#205): Deren Rhythmus gilt, ein
   * eigener ist gesperrt. Der Bereich sagt das, statt einen Wähler anzubieten.
   */
  recurrenceLocked?: boolean
}

/**
 * **Ein Bereich im Rumpf der Aufgaben-Details** — Überschrift, bei Bedarf ein Knopf
 * zum Abnehmen, darunter der Inhalt. Seit dem 19.09.2026 sind es genau vier: Wann,
 * Deadline, Dauer, Wiederkehrend. Titel, Notiz, Unteraufgaben und die drei
 * Einordnungs-Werte stehen im **Kopf** (`DetailsHeader`).
 *
 * Die Überschrift trägt die **Wellenlinie** des Hauses — dasselbe Trenner-Motiv wie
 * am **Abschnittskopf** der Liste und im Wiederkehr-Kasten. Unterscheidbar bleiben
 * sie über die Schrift: der Abschnittskopf in Satzschrift, der Bereichskopf in
 * kleinen Versalien.
 *
 * Die Inhalte sind dieselben Komponenten, die das **Aufgaben-Menü** und die **Marken**
 * an der Zeile benutzen; hier tragen sie `placement="inline"`: kein Fokus beim Aufbau, kein
 * Aufschieben hinter ein Schließen, kein „Bestätigen" — nur „Entfernen", sobald ein
 * Wert dasteht (ADR 0026).
 *
 * Die Werte kommen aus `values` und gehören der ganzen AUSWAHL; geschrieben wird über
 * `anchorId`, und `onUpdate` weitet von dort auf die übrigen aus.
 *
 * @example
 * <DetailsSection section={section} anchorId={anchor.id} values={values} onUpdate={onUpdate} />
 */
export const DetailsSection: React.FC<DetailsSectionProps> = ({
  section, anchorId, values, onUpdate, focusedSeconds, onRest, resting, recurrenceLocked = false,
}) => {
  const { t } = useTranslation()
  const label = t(section.labelKey)
  const id = section.id as DetailsBodyId

  const patch = REMOVE_PATCH[id]
  const handleRemove = useCallback(() => {
    if (patch) onUpdate(anchorId, patch)
  }, [patch, onUpdate, anchorId])

  /**
   * **Eine Wiederholung ohne Termin steht nirgends im Kalender.**
   *
   * Ein fester Rhythmus an einer Aufgabe OHNE „Wann" bekommt deshalb den ersten
   * Termin mitgeschrieben — dieselbe Rechnung, die die aufgeklappte Karte
   * (`TaskEntry`), das Formular (`useTaskEntryForm`) und die Seite
   * **Wiederkehrend** (`RecurringCreator`) schon anstellen. Bis zum 19.09.2026
   * fehlte sie hier als einzige, und dieselbe Aufgabe bekam je nach Weg einen
   * Termin oder keinen.
   *
   * **Die Regel setzt das Wann** (Nutzer-Entscheidung 21.09.2026) — jede
   * gültige Änderung rechnet den Termin neu, auch wenn dort schon einer stand.
   *
   * Hier lag dafür ein Merker (`ruleStart`): Er unterschied einen Termin, den
   * die Regel gesetzt hatte, von einem, den der Nutzer selbst gewählt hatte —
   * nötig, solange ein eigenes Wann unantastbar war (Regel vom 12.09.). Mit
   * deren Wegfall unterscheidet nichts mehr etwas: Es wird immer neu gerechnet.
   * Das löst nebenbei, was beim Sofort-Schreiben ohnehin gebraucht wurde —
   * „Wöchentlich" setzt den Wochentag von heute, der nächste Griff macht
   * daraus den Freitag, und der Kalender zeigt den Freitag.
   */
  const handleRecurrenceChange = useCallback((rule: RecurrenceRule | null) => {
    onUpdate(anchorId, recurrenceUpdate(rule, new Date()))
  }, [onUpdate, anchorId])

  const content = (() => {
    switch (id) {
      case 'start':
        return (
          <WhenDropdownContent
            value={values.start}
            placement="inline"
            onClose={noop}
            onValueChange={v => onUpdate(anchorId, {
              startDate: v?.date ?? null, startTime: v?.time ?? null, startPeriod: v?.period ?? null,
            })}
            // Der Griff nur, wenn etwas dasteht — ohne Wert gibt es keinen Fuß.
            onRemove={values.start ? handleRemove : undefined}
            onRest={onRest}
            resting={resting}
          />
        )
      case 'deadline':
        return (
          <DeadlineDropdownContent
            value={values.deadline}
            placement="inline"
            onClose={noop}
            onValueChange={v => onUpdate(anchorId, {
              dueDate: v?.date ?? null, dueTime: v?.time ?? null, duePeriod: v?.period ?? null,
            })}
            onRemove={values.deadline ? handleRemove : undefined}
          />
        )
      case 'duration':
        return (
          <DurationDropdownContent
            value={values.duration}
            placement="inline"
            onClose={noop}
            onValueChange={v => onUpdate(anchorId, {
              estimatedDuration: v?.minutes ?? null, durationMode: v?.mode ?? null,
            })}
            // Die gelaufene Zeit steht IM Werkzeug, neben der veranschlagten:
            // „9 Min 53 Sek von 10 Min". Getrennt untereinander sagte keine
            // der beiden Zahlen etwas über die andere.
            focusedSeconds={focusedSeconds}
            // Der Griff nur, wenn etwas dasteht — ohne Wert gibt es keinen Fuß.
            onRemove={values.duration ? handleRemove : undefined}
          />
        )
      case 'recurrence':
        if (recurrenceLocked) {
          return <p className="details-section__hint">{t('taskDetails.recurrenceWithParent')}</p>
        }
        return (
          <RecurrenceDropdownContent
            value={values.recurrence}
            seedDate={values.start?.date ?? null}
            placement="inline"
            onClose={noop}
            onValueChange={handleRecurrenceChange}
            // „Nie" im Auswahlfeld ist der Weg zurück — hier gibt es keinen Fuß.
            onClear={handleRemove}
          />
        )
    }
  })()

  return (
    <section
      className={`details-section${BOXED.has(id) ? ' details-section--boxed' : ''}`}
      data-section={section.id}
    >
      <header className="details-section__head">
        <h3 className="details-section__label">{label}</h3>
        <DecorativeWave className="details-section__wave" />
      </header>
      <div className="details-section__body">{content}</div>
    </section>
  )
}
